/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	// '../renderer/topbottom/TopBottomLabel',
	'../../../../visualizations/renderer/topbottom/TopBottomLabel',
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../api/SlotActionsProviderAPI',
	'../../../../apiHelpers/SlotAPIHelper',
	'../../../../widgets/livewidget/nls/StringResources'
], function(TopBottomLabel, APIFactory, SlotActionsProviderAPI, SlotAPIHelper, stringResources) {
	'use strict';

	const TOP_OP = 'top';
	const TOP_TYPE = 'topcount';

	const ACTION_ID = 'topKeyDriversAction';
	const ACTION_ICON = 'dashboard-topbottom';
	const ACTION_ITEMS = {
		TOP_5: 'top5',
		TOP_10: 'top10',
		ALL: 'all'
	};

	return class TopKeyDriversAction extends TopBottomLabel {

		constructor({ content, dashboardAPI, features }) {
			super();
			this.content = content;
			this.dashboard = dashboardAPI;
			this.features = features;
			features.SlotActions.registerProvider('topKeyDriversAction', this.getAPI());
		}

		getAPI() {
			if (!this._api) {
				this._api = APIFactory.createAPI(this, [SlotActionsProviderAPI]);
			}
			return this._api;
		}

		_isActionSupported(dataSlot, mapIndex) {
			if (!this._isFocusModeEnabled()) {
				return false;
			}
			const actions = dataSlot.getDefinition().getProperty('actions');
			if (!actions) {
				return false;
			}
			if (actions.indexOf('TopKeyDriversAction') === -1) {
				return false;
			}
			return !this._isMultiMeasureDataSlot(dataSlot, mapIndex);
		}

		_isFocusModeEnabled() {
			return !this.dashboard.getGlassCoreSvc('.FeatureChecker').checkValue('dashboard', 'FocusMode', 'disabled');
		}

		getViewState(dataSlotId, itemIndex) {
			const dataSlot = this._getVisualization().getSlots().getSlot(dataSlotId);
			if(dataSlot) {
				return {
					height: 140,
					width: 140,
					getCurrentSelectedItem: this._getCurrentSelectedItem.bind(this, dataSlot, itemIndex),
					possibleItems: this._getPossibleItems(dataSlot, itemIndex),
					eventHandler: this
				};
			}
			return null;
		}

		/**
		 * Initialize the set of TopKeyDriversAction settings the selected state
		 */
		getSlotActionList(dataSlotId, itemIndex = 0) {
			this.dataSlot = this._getVisualization().getSlots().getSlot(dataSlotId);
			this.itemIndex = itemIndex;
			if ( this.dataSlot && this._isActionSupported(this.dataSlot, itemIndex)){
				const actionIcon = this.features['Dashboard.Icons'].getIcon(ACTION_ICON);
				const dataItem = this.dataSlot.getDataItemList()[itemIndex];
				return [{
					name: 'show',
					targetCaption: dataItem && dataItem.getLabel(),
					id: 'TopKeyDriversAction',
					text: stringResources.get('toolbarActionTopKeyDrivers'),
					label: stringResources.get('toolbarActionTopKeyDrivers'),
					icon: actionIcon.id,
					type: 'NextView',
					group: ACTION_ID,
					view: {
						module: 'dashboard-analytics/lib/@waca/dashboard-common/dist/ui/dialogs/CommonSelectItemsDialog',
						state: this.getViewState(dataSlotId, itemIndex)
					},
					actions: {
						apply: this.handleSelection.bind(this)
					}
				}];
			}
			return [];
		}

		_getCurrentSelectedItem(dataSlot, itemIndex = 0) {
			const dataItem = dataSlot.getDataItemList()[itemIndex];
			const state = dataItem.getTopBottom();
			if (state && state.type === TOP_TYPE) {
				return state.value === 5 ? ACTION_ITEMS.TOP_5 : ACTION_ITEMS.TOP_10;
			}
			return ACTION_ITEMS.ALL;
		}

		/**
		 * Get the array of possible items to be displayed on the dialog
		 * @return array of items to display on dialog
		 */
		_getPossibleItems() {
			return [
				{
					id: ACTION_ITEMS.ALL,
					localizedString: stringResources.get('allKeyDriversLabel')
				},
				{
					id: ACTION_ITEMS.TOP_5,
					localizedString: this.getTopBottomLabel({
						limit: {
							op: TOP_OP,
							val: 5
						}
					})
				},
				{
					id: ACTION_ITEMS.TOP_10,
					localizedString: this.getTopBottomLabel({
						limit: {
							op: TOP_OP,
							val: 10
						}
					})
				}
			];
		}

		_isMultiMeasureDataSlot (dataSlot, itemIndex) {
			if(itemIndex === undefined && dataSlot){
				return SlotAPIHelper.isMultiMeasuresSeriesSlot(dataSlot) || SlotAPIHelper.isMultiMeasuresValueSlot(dataSlot);
			}

			return SlotAPIHelper.isMultiMeasuresSeriesOrValue(dataSlot, itemIndex);
		}

		_getVisualization() {
			if (!this.visualization) {
				this.visualization = this.content.getFeature('Visualization');
			}
			return this.visualization;
		}

		/**
		 * Handler to the item selection.
		 * @param {string} selectedItem - identifier of the item selected
		 * @see {@link ACTION_ITEMS}
		 */
		handleSelection(selectedItem) {
			const dataItem = this.dataSlot.getDataItemList()[this.itemIndex];
			switch (selectedItem) {
				case ACTION_ITEMS.ALL:
					dataItem.setTopBottom();
					break;
				case ACTION_ITEMS.TOP_5:
				case ACTION_ITEMS.TOP_10:
					dataItem.setTopBottom({
						type: TOP_TYPE,
						value: selectedItem === ACTION_ITEMS.TOP_5 ? 5 : 10
					});
					break;
				default:
					break;
			}
		}
	};
});
